import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./ui/select";
import { toast } from "sonner";
import { 
  HardHat, 
  Landmark, 
  Send, 
  FileText, 
  DollarSign, 
  Users, 
  Clock,
  CheckCircle,
  ArrowRight,
  Building2,
  Home,
  Gavel
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stage-based workflow actions
const WORKFLOW_ACTIONS = {
  contracted: {
    primary: {
      label: "Request Rehab Estimate",
      icon: HardHat,
      nextStage: "rehab_estimate",
      description: "Send to contractors for scope of work pricing"
    },
    secondary: {
      label: "Skip to Title",
      icon: Landmark,
      nextStage: "sent_to_title",
      description: "No rehab needed - proceed directly to title"
    }
  },
  rehab_estimate: {
    primary: {
      label: "Send to Title",
      icon: Landmark,
      nextStage: "sent_to_title",
      description: "Send contract package to title company"
    }
  },
  sent_to_title: {
    primary: {
      label: "Mark Title Processing",
      icon: FileText,
      nextStage: "title_processing",
      description: "Title company has started processing"
    }
  },
  title_processing: {
    primary: {
      label: "Ready to Close",
      icon: CheckCircle,
      nextStage: "ready_to_close",
      description: "All title work complete, ready for closing"
    }
  },
  ready_to_close: {
    primary: {
      label: "Close Deal",
      icon: DollarSign,
      nextStage: "closed",
      description: "Record closing completion"
    }
  },
  closed: {
    primary: {
      label: "Post for Contractors",
      icon: HardHat,
      nextStage: "posting_contractors",
      description: "Post renovation job for contractor bids"
    },
    secondary: {
      label: "Skip to Realtor",
      icon: Home,
      nextStage: "finding_realtor",
      description: "No rehab needed - find realtor to list"
    }
  },
  posting_contractors: {
    primary: {
      label: "Select Contractor",
      icon: Users,
      nextStage: "contractor_selected",
      description: "Award job to winning contractor"
    }
  },
  contractor_selected: {
    primary: {
      label: "Find Realtor",
      icon: Home,
      nextStage: "finding_realtor",
      description: "Begin realtor search for listing"
    }
  },
  finding_realtor: {
    primary: {
      label: "List Property",
      icon: Building2,
      nextStage: "listed",
      description: "Property now listed on market"
    }
  },
  listed: {
    primary: {
      label: "Under Contract",
      icon: FileText,
      nextStage: "under_contract_sale",
      description: "Accepted offer, under contract to buyer"
    }
  },
  under_contract_sale: {
    primary: {
      label: "Mark Sold",
      icon: CheckCircle,
      nextStage: "sold",
      description: "Close sale and finalize transaction"
    }
  }
};

export default function WorkflowActions({ deal, onDealUpdate }) {
  const [showRehabModal, setShowRehabModal] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Rehab estimate form
  const [rehabForm, setRehabForm] = useState({
    scope_of_work: "",
    estimated_budget: "",
    timeline_weeks: "",
    urgency: "standard",
    notes: ""
  });
  
  // Title selection form
  const [titleForm, setTitleForm] = useState({
    title_company_id: "",
    title_company_name: "",
    enable_bidding: false,
    notes: ""
  });

  const currentStage = deal?.pipeline_stage;
  const actions = WORKFLOW_ACTIONS[currentStage];
  
  if (!actions) return null;

  const handleAdvanceStage = async (nextStage, additionalData = {}) => {
    setIsSubmitting(true);
    try {
      const updateData = {
        pipeline_stage: nextStage,
        ...additionalData
      };
      
      await axios.put(
        `${BACKEND_URL}/api/deals/${deal.deal_id}`,
        updateData,
        { withCredentials: true }
      );
      
      toast.success(`Transaction moved to ${nextStage.replace(/_/g, ' ').toUpperCase()}`);
      onDealUpdate?.({ ...deal, pipeline_stage: nextStage, ...additionalData });
    } catch (error) {
      toast.error("Failed to update transaction stage");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRehabRequest = async () => {
    // Update the deal with rehab info and move to next stage
    const additionalData = {
      rehab_scope: rehabForm.scope_of_work,
      rehab_budget_estimate: parseFloat(rehabForm.estimated_budget) || 0,
      rehab_timeline_weeks: parseInt(rehabForm.timeline_weeks) || 0,
      rehab_urgency: rehabForm.urgency,
      rehab_notes: rehabForm.notes
    };
    
    await handleAdvanceStage("rehab_estimate", additionalData);
    setShowRehabModal(false);
  };

  const handleTitleSend = async () => {
    const additionalData = {
      title_company_name: titleForm.title_company_name,
      title_bidding_enabled: titleForm.enable_bidding,
      title_notes: titleForm.notes
    };
    
    await handleAdvanceStage("sent_to_title", additionalData);
    setShowTitleModal(false);
  };

  const handlePrimaryAction = () => {
    if (currentStage === "contracted") {
      setShowRehabModal(true);
    } else if (currentStage === "rehab_estimate") {
      setShowTitleModal(true);
    } else {
      handleAdvanceStage(actions.primary.nextStage);
    }
  };

  const handleSecondaryAction = () => {
    if (actions.secondary) {
      if (actions.secondary.nextStage === "sent_to_title") {
        setShowTitleModal(true);
      } else {
        handleAdvanceStage(actions.secondary.nextStage);
      }
    }
  };

  return (
    <>
      {/* Workflow Actions Bar */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-md p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">
              Next Step in Flow
            </h3>
            <p className="text-sm text-white/50">{actions.primary.description}</p>
          </div>
          
          <div className="flex items-center gap-3">
            {actions.secondary && (
              <Button
                variant="outline"
                className="border-white/[0.08] text-white/70 hover:bg-[#0A0A0A]/80 backdrop-blur-sm hover:text-white"
                onClick={handleSecondaryAction}
                disabled={isSubmitting}
              >
                <actions.secondary.icon className="w-4 h-4 mr-2" />
                {actions.secondary.label}
              </Button>
            )}
            
            <Button
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
              onClick={handlePrimaryAction}
              disabled={isSubmitting}
            >
              <actions.primary.icon className="w-4 h-4 mr-2" />
              {actions.primary.label}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Rehab Estimate Request Modal */}
      <Dialog open={showRehabModal} onOpenChange={setShowRehabModal}>
        <DialogContent className="bg-black border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <HardHat className="w-5 h-5 text-[#D4AF37]" />
              Request Rehab Estimate
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/70">Scope of Work</Label>
              <Textarea
                value={rehabForm.scope_of_work}
                onChange={(e) => setRehabForm({ ...rehabForm, scope_of_work: e.target.value })}
                placeholder="Describe the renovation scope: kitchen remodel, flooring, paint, etc."
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Estimated Budget</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="number"
                    value={rehabForm.estimated_budget}
                    onChange={(e) => setRehabForm({ ...rehabForm, estimated_budget: e.target.value })}
                    placeholder="50000"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white pl-9"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/70">Timeline (weeks)</Label>
                <div className="relative mt-1">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="number"
                    value={rehabForm.timeline_weeks}
                    onChange={(e) => setRehabForm({ ...rehabForm, timeline_weeks: e.target.value })}
                    placeholder="8"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white pl-9"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-white/70">Urgency</Label>
              <Select
                value={rehabForm.urgency}
                onValueChange={(v) => setRehabForm({ ...rehabForm, urgency: v })}
              >
                <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                  <SelectItem value="standard" className="text-white">Standard</SelectItem>
                  <SelectItem value="asap" className="text-white">ASAP</SelectItem>
                  <SelectItem value="urgent" className="text-white">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-white/70">Notes for Contractors</Label>
              <Textarea
                value={rehabForm.notes}
                onChange={(e) => setRehabForm({ ...rehabForm, notes: e.target.value })}
                placeholder="Any special instructions or access details..."
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
              <Button
                variant="outline"
                onClick={() => setShowRehabModal(false)}
                className="border-white/[0.08] text-white/70"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRehabRequest}
                disabled={isSubmitting}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
              >
                {isSubmitting ? "Submitting..." : "Request Estimates"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Title Modal */}
      <Dialog open={showTitleModal} onOpenChange={setShowTitleModal}>
        <DialogContent className="bg-black border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Landmark className="w-5 h-5 text-[#D4AF37]" />
              Send to Title Company
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] rounded">
              <p className="text-sm text-white/50 mb-3">Choose how to select a title company:</p>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setTitleForm({ ...titleForm, enable_bidding: false })}
                  className={`p-4 border rounded text-left transition ${
                    !titleForm.enable_bidding 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                      : 'border-white/[0.08] hover:border-white/[0.1]'
                  }`}
                >
                  <Building2 className={`w-5 h-5 mb-2 ${!titleForm.enable_bidding ? 'text-[#D4AF37]' : 'text-white/50'}`} />
                  <p className={`text-sm font-medium ${!titleForm.enable_bidding ? 'text-[#D4AF37]' : 'text-white'}`}>
                    Select Company
                  </p>
                  <p className="text-xs text-white/40 mt-1">Choose from your network</p>
                </button>
                
                <button
                  onClick={() => setTitleForm({ ...titleForm, enable_bidding: true })}
                  className={`p-4 border rounded text-left transition ${
                    titleForm.enable_bidding 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                      : 'border-white/[0.08] hover:border-white/[0.1]'
                  }`}
                >
                  <Gavel className={`w-5 h-5 mb-2 ${titleForm.enable_bidding ? 'text-[#D4AF37]' : 'text-white/50'}`} />
                  <p className={`text-sm font-medium ${titleForm.enable_bidding ? 'text-[#D4AF37]' : 'text-white'}`}>
                    Request Bids
                  </p>
                  <p className="text-xs text-white/40 mt-1">Let companies compete</p>
                </button>
              </div>
            </div>
            
            {!titleForm.enable_bidding && (
              <div>
                <Label className="text-white/70">Title Company Name</Label>
                <Input
                  value={titleForm.title_company_name}
                  onChange={(e) => setTitleForm({ ...titleForm, title_company_name: e.target.value })}
                  placeholder="Enter title company name"
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                />
              </div>
            )}
            
            {titleForm.enable_bidding && (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded">
                <p className="text-sm text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                  Title companies in the investor's area will be notified and can submit their rates.
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-white/70">Notes</Label>
              <Textarea
                value={titleForm.notes}
                onChange={(e) => setTitleForm({ ...titleForm, notes: e.target.value })}
                placeholder="Any special instructions for the title company..."
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
              <Button
                variant="outline"
                onClick={() => setShowTitleModal(false)}
                className="border-white/[0.08] text-white/70"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTitleSend}
                disabled={isSubmitting || (!titleForm.enable_bidding && !titleForm.title_company_name)}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
              >
                {isSubmitting ? "Sending..." : titleForm.enable_bidding ? "Post for Bids" : "Send to Title"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { WORKFLOW_ACTIONS };
